import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Lightbulb, 
  Clock, 
  CheckCircle2, 
  Sparkles,
  MessageSquare,
  Rocket,
  User,
  Building2,
  Filter
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AgencySuggestions = () => {
  const [suggestions, setSuggestions] = useState([]);
  const [counts, setCounts] = useState({ total: 0, pending: 0, acknowledged: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [acknowledgeDialogOpen, setAcknowledgeDialogOpen] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [notes, setNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchSuggestions();
  }, []);

  const fetchSuggestions = async () => {
    try {
      const response = await axios.get(`${API_URL}/suggestions`);
      setSuggestions(response.data.suggestions);
      setCounts(response.data.counts);
    } catch (error) {
      toast.error('Failed to load suggestions');
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async () => {
    if (!selectedSuggestion) return;
    
    setProcessing(true);
    try {
      await axios.patch(`${API_URL}/suggestions/${selectedSuggestion.id}/acknowledge`, {
        notes: notes || null
      });
      toast.success('Suggestion acknowledged! Client has been notified.');
      setAcknowledgeDialogOpen(false);
      setSelectedSuggestion(null);
      setNotes('');
      fetchSuggestions();
    } catch (error) {
      toast.error('Failed to acknowledge suggestion');
    } finally {
      setProcessing(false);
    }
  };

  const handleStatusChange = async (suggestionId, newStatus) => {
    try {
      await axios.patch(`${API_URL}/suggestions/${suggestionId}/status?status=${newStatus}`);
      toast.success(`Status updated to ${newStatus}`);
      fetchSuggestions();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const openAcknowledgeDialog = (suggestion) => {
    setSelectedSuggestion(suggestion);
    setNotes('');
    setAcknowledgeDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return (
          <Badge className="bg-amber-100 text-amber-700 border-amber-200">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </Badge>
        );
      case 'acknowledged':
        return (
          <Badge className="bg-[#d8e5b8] text-[#3d4f22] border-[#b8cf88]">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Acknowledged
          </Badge>
        );
      case 'implemented':
        return (
          <Badge className="bg-[#d8e5b8] text-[#3d4f22] border-[#b8cf88]">
            <Rocket className="w-3 h-3 mr-1" />
            Implemented
          </Badge>
        );
      case 'declined':
        return (
          <Badge className="bg-slate-100 text-slate-600 border-slate-200">
            Declined
          </Badge>
        );
      default:
        return null;
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'feature':
        return <Sparkles className="w-4 h-4 text-purple-500" />;
      case 'improvement':
        return <Rocket className="w-4 h-4 text-[#556B2F]" />;
      case 'content_idea':
        return <Lightbulb className="w-4 h-4 text-amber-500" />;
      default:
        return <MessageSquare className="w-4 h-4 text-slate-500" />;
    }
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case 'feature': return 'Feature Request';
      case 'improvement': return 'Improvement';
      case 'content_idea': return 'Content Idea';
      default: return 'Other';
    }
  };

  const filteredSuggestions = suggestions.filter(s => {
    if (filter === 'all') return true;
    return s.status === filter;
  });

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#556B2F]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="agency-suggestions">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
            Client Ideas & Suggestions
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Review and acknowledge client suggestions
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 cursor-pointer hover:border-slate-300 transition-colors" onClick={() => setFilter('all')}>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
              <Lightbulb className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{counts.total}</p>
              <p className="text-sm text-slate-500">Total</p>
            </div>
          </CardContent>
        </Card>
        <Card className={`bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 cursor-pointer hover:border-amber-300 transition-colors ${filter === 'pending' ? 'ring-2 ring-amber-400' : ''}`} onClick={() => setFilter('pending')}>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{counts.pending}</p>
              <p className="text-sm text-slate-500">Pending</p>
            </div>
          </CardContent>
        </Card>
        <Card className={`bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 cursor-pointer hover:border-[#91a85e] transition-colors ${filter === 'acknowledged' ? 'ring-2 ring-[#556B2F]' : ''}`} onClick={() => setFilter('acknowledged')}>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#d8e5b8] dark:bg-[#556B2F]/15 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-[#3d4f22] dark:text-[#b8cf88]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{counts.acknowledged}</p>
              <p className="text-sm text-slate-500">Acknowledged</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#d8e5b8] dark:bg-[#556B2F]/15 flex items-center justify-center">
              <Rocket className="w-5 h-5 text-[#556B2F]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">
                {suggestions.filter(s => s.status === 'implemented').length}
              </p>
              <p className="text-sm text-slate-500">Implemented</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      {filter !== 'all' && (
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          <span className="text-sm text-slate-500">Filtered by: {filter}</span>
          <Button variant="ghost" size="sm" onClick={() => setFilter('all')}>
            Clear filter
          </Button>
        </div>
      )}

      {/* Suggestions List */}
      {filteredSuggestions.length === 0 ? (
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <CardContent className="py-12 text-center">
            <Lightbulb className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
              {filter === 'all' ? 'No suggestions yet' : `No ${filter} suggestions`}
            </h3>
            <p className="text-slate-500">
              {filter === 'all' 
                ? 'When clients submit ideas, they will appear here'
                : 'Try a different filter'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredSuggestions.map((suggestion) => (
            <Card 
              key={suggestion.id} 
              className={`bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 ${suggestion.status === 'pending' ? 'border-l-4 border-l-amber-400' : ''}`}
              data-testid={`suggestion-${suggestion.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    {/* Header */}
                    <div className="flex items-center gap-3 mb-2">
                      <div className="flex items-center gap-2">
                        {getCategoryIcon(suggestion.category)}
                        <span className="text-xs text-slate-500 uppercase tracking-wide">
                          {getCategoryLabel(suggestion.category)}
                        </span>
                      </div>
                      {getStatusBadge(suggestion.status)}
                    </div>
                    
                    {/* Title */}
                    <h3 className="font-semibold text-lg text-slate-900 dark:text-white mb-2">
                      {suggestion.title}
                    </h3>
                    
                    {/* Description */}
                    <p className="text-slate-600 dark:text-slate-400 mb-4">
                      {suggestion.description}
                    </p>
                    
                    {/* Meta */}
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <div className="flex items-center gap-1">
                        <Building2 className="w-4 h-4" />
                        <span>{suggestion.client_name || 'Unknown Client'}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        <span>{suggestion.user_name || 'Unknown User'}</span>
                      </div>
                      <span>{new Date(suggestion.created_at).toLocaleDateString()}</span>
                    </div>

                    {/* Agency Notes */}
                    {suggestion.agency_notes && (
                      <div className="mt-4 p-3 bg-[#f0f4e8] dark:bg-[#556B2F]/10 rounded-lg border border-[#d8e5b8] dark:border-[#3d4f22]">
                        <p className="text-xs text-[#3d4f22] dark:text-[#6B8E23] mb-1 font-medium">Your Response:</p>
                        <p className="text-sm text-slate-700 dark:text-slate-300">{suggestion.agency_notes}</p>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2">
                    {suggestion.status === 'pending' && (
                      <Button 
                        onClick={() => openAcknowledgeDialog(suggestion)}
                        className="bg-[#556B2F] hover:bg-[#3d4f22] text-white"
                        data-testid={`acknowledge-btn-${suggestion.id}`}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Acknowledge
                      </Button>
                    )}
                    <Select 
                      value={suggestion.status} 
                      onValueChange={(v) => handleStatusChange(suggestion.id, v)}
                    >
                      <SelectTrigger className="w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="acknowledged">Acknowledged</SelectItem>
                        <SelectItem value="implemented">Implemented</SelectItem>
                        <SelectItem value="declined">Declined</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Acknowledge Dialog */}
      <Dialog open={acknowledgeDialogOpen} onOpenChange={setAcknowledgeDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-[#556B2F]" />
              Acknowledge Suggestion
            </DialogTitle>
            <DialogDescription>
              Let the client know you've seen their idea. Add an optional note.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <p className="font-medium text-sm">{selectedSuggestion?.title}</p>
              <p className="text-xs text-slate-500 mt-1">From: {selectedSuggestion?.client_name}</p>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Response (Optional)</label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Thanks for the suggestion! We'll consider this..."
                rows={3}
              />
            </div>

            <div className="flex gap-3">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setAcknowledgeDialogOpen(false)} 
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleAcknowledge}
                disabled={processing}
                className="flex-1 bg-[#556B2F] hover:bg-[#3d4f22] text-white"
              >
                {processing ? 'Sending...' : 'Acknowledge'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AgencySuggestions;
